import { defineStore } from "pinia";
import { emit, GameEvents } from "../game/events.js";
import { useGameStore } from "./gameStore.js";
import { useInventoryStore } from "./inventoryStore.js";
import { useUpgradeStore } from "./upgradeStore.js";

export const WorkflowEvents = {
  ON_DROP: "onDrop",
  ON_SMITHED: "onSmithed",
  ON_SMELTED: "onSmelted",
  ALWAYS: "always",
};

export const WorkflowFilters = {
  AVERAGE_BELOW: "averageBelow",
  AVERAGE_ABOVE: "averageAbove",
  SUBTYPE_IS: "subtypeIs",
  MATERIAL_IS: "materialIs",
  DPS_BELOW_EQUIPPED: "dpsBelowEquipped",
  DPS_ABOVE_EQUIPPED: "dpsAboveEquipped",
};

export const WorkflowActions = {
  SMELT: "smelt",
  CREATE: "create",
  EQUIP: "equip",
};

export const useWorkflowStore = defineStore("workflow", {
  state: () => ({
    workflows: [],
    smithingQueue: [],
    smeltingQueue: [],
    isSmithing: false,
    isSmelting: false,
    smithingProgress: 0,
    smeltingProgress: 0,
    smithingTime: 180,
    smeltingTime: 180,
  }),
  getters: {
    smithingProgressPercent: (state) => {
      const upgrades = useUpgradeStore();
      const craftTime = upgrades.hasPurchased("smithy_craft_speed") ? 90 : 180;
      return (state.smithingProgress / craftTime) * 100;
    },
    smeltingProgressPercent: (state) => {
      const upgrades = useUpgradeStore();
      const smeltTime = upgrades.hasPurchased("smithy_smelt_speed") ? 90 : 180;
      return (state.smeltingProgress / smeltTime) * 100;
    },
  },
  actions: {
    addWorkflow(workflow) {
      workflow.id = Date.now();
      this.workflows.push(workflow);
    },
    removeWorkflow(id) {
      this.workflows = this.workflows.filter((w) => w.id !== id);
    },
    clearSmeltingQueue() {
      const inventoryStore = useInventoryStore();
      for (const item of this.smeltingQueue) {
        inventoryStore.addItem(item);
      }
      this.smeltingQueue = [];
      this.isSmelting = false;
      this.smeltingProgress = 0;
      emit(GameEvents.SMELT_QUEUE_CLEARED, {});
    },
    clearSmithingQueue() {
      this.smithingQueue = [];
      this.isSmithing = false;
      this.smithingProgress = 0;
      emit(GameEvents.SMITH_QUEUE_CLEARED, {});
    },
    queueSmelt(item) {
      emit(GameEvents.SMELT_QUEUED, { item });
    },
    cancelSmelt(item) {
      emit(GameEvents.SMELT_CANCELLED, { item });
    },
    queueSmith(recipe) {
      emit(GameEvents.SMITH_QUEUED, { recipe });
    },
    cancelSmith(index) {
      this.smithingQueue.splice(index, 1);
    },
    processSmithingTick() {
      emit(GameEvents.SMITH_TICK);
    },
    processSmeltingTick() {
      emit(GameEvents.SMELT_TICK);
    },
    checkItemAgainstWorkflows(event, item) {
      for (const workflow of this.workflows) {
        if (
          workflow.event !== event &&
          workflow.event !== WorkflowEvents.ALWAYS
        )
          continue;

        if (!this.matchesFilter(workflow, item)) continue;

        this.executeAction(workflow.action, item, workflow);
      }
    },
    processWorkflows(event, item) {
      this.checkItemAgainstWorkflows(event, item);
    },
    matchesFilter(workflow, item) {
      const filter = workflow.filter;
      if (!filter || !filter.type) return true;

      const rolls = Object.values(item.rolls);
      const average = rolls.reduce((a, b) => a + b, 0) / rolls.length;

      switch (filter.type) {
        case WorkflowFilters.AVERAGE_BELOW:
          return average < filter.value;
        case WorkflowFilters.AVERAGE_ABOVE:
          return average > filter.value;
        case WorkflowFilters.SUBTYPE_IS:
          return item.subtype.name === filter.value;
        case WorkflowFilters.MATERIAL_IS:
          return item.material.name === filter.value;
        case WorkflowFilters.DPS_BELOW_EQUIPPED: {
          const gameStore = useGameStore();
          if (!gameStore.player.weapon) return false;
          return item.dps < gameStore.player.weapon.dps;
        }
        case WorkflowFilters.DPS_ABOVE_EQUIPPED: {
          const gameStore = useGameStore();
          if (!gameStore.player.weapon) return true;
          return item.dps > gameStore.player.weapon.dps;
        }
        default:
          return true;
      }
    },
    executeAction(action, item, workflow) {
      const inventoryStore = useInventoryStore();
      switch (action) {
        case WorkflowActions.SMELT:
          this.queueSmelt(item);
          break;
        case WorkflowActions.CREATE:
          this.queueSmith({
            material: workflow.material,
            subtype: workflow.subtype,
          });
          break;
        case WorkflowActions.EQUIP: {
          const gameStore = useGameStore();
          const oldWeapon = gameStore.player.weapon;
          const index = inventoryStore.items.indexOf(item);
          if (index !== -1) {
            inventoryStore.items.splice(index, 1);
          }
          gameStore.equipWeapon(item);
          if (oldWeapon) {
            inventoryStore.addItem(oldWeapon);
          }
          break;
        }
      }
    },
    runAlwaysWorkflows() {
      const inventory = useInventoryStore();
      for (const item of [...inventory.items]) {
        for (const workflow of this.workflows) {
          if (workflow.event !== WorkflowEvents.ALWAYS) continue;
          if (workflow.action === WorkflowActions.CREATE) continue;
          if (!this.matchesFilter(workflow, item)) continue;
          if (this.smeltingQueue.includes(item)) continue;
          this.executeAction(workflow.action, item, workflow);
        }
      }
      for (const workflow of this.workflows) {
        if (workflow.event !== WorkflowEvents.ALWAYS) continue;
        if (workflow.action !== WorkflowActions.CREATE) continue;
        if (
          this.smithingQueue.some(
            (r) =>
              r.material === workflow.material && r.subtype === workflow.subtype
          )
        )
          continue;
        this.queueSmith({
          material: workflow.material,
          subtype: workflow.subtype,
        });
      }
    },
  },
});
